// /identities/:id — detalhe do cliente (Story 2.6).
// Header + stats + 9 tabs com deep link ?tab=X.

import { useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { formatDayBR } from '@control-center/shared';

import { ClientChargesTab } from '../../components/charges/ClientChargesTab';
import { ClientPaymentsTab } from '../../components/charges/ClientPaymentsTab';
import { AccessOverrideActions } from '../../components/clients/AccessOverrideActions';
import { CancelClientDialog } from '../../components/clients/CancelClientDialog';
import { ClientHeader } from '../../components/clients/ClientHeader';
import { ClientSubscriptionsTab } from '../../components/clients/ClientSubscriptionsTab';
import { ClientTableSkeleton } from '../../components/clients/ClientTable';
import { ClientTabs, useActiveTab } from '../../components/clients/ClientTabs';
import { PillButton } from '../../components/clients/PillButton';
import { DataTab } from '../../components/clients/tabs/DataTab';
import { LogsTab } from '../../components/clients/tabs/LogsTab';
import { SaasLinksTab } from '../../components/clients/tabs/SaasLinksTab';
import { CommunicationsList } from '../../components/communications/CommunicationsList';
import { ContactsTab } from '../../components/contacts/ContactsTab';
import { useCharges } from '../../hooks/use-charges';
import { useCancelClient, useClient } from '../../hooks/use-clients';
import { useSubscriptions } from '../../hooks/use-subscriptions';
import { extractErrorMessage } from '../../lib/errors';

function formatBRL(amount: number): string {
  return amount.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}

const ACTIVE_SUBSCRIPTION_STATUSES = new Set<string>(['ativa', 'em_teste']);

export function ClientDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [cancelOpen, setCancelOpen] = useState(false);
  const { active } = useActiveTab();

  const { data: client, isLoading, isError, error, refetch } = useClient(id);
  const cancel = useCancelClient();
  const subscriptionsQ = useSubscriptions(id ? { client_id: id, limit: 50 } : { limit: 0 });
  const chargesQ = useCharges(
    id
      ? { scope: 'todas', clientId: id, page: 1, pageSize: 1 }
      : { scope: 'todas', page: 1, pageSize: 1 },
  );

  const subscriptionsItems = useMemo(() => subscriptionsQ.data?.items ?? [], [subscriptionsQ.data]);
  const activeSubs = useMemo(
    () => subscriptionsItems.filter((s) => ACTIVE_SUBSCRIPTION_STATUSES.has(s.status)),
    [subscriptionsItems],
  );
  const mrrContributed = useMemo(
    () =>
      activeSubs.reduce((sum, s) => {
        const value = Number.parseFloat(s.price);
        return sum + (Number.isFinite(value) ? value : 0);
      }, 0),
    [activeSubs],
  );
  const lastCharge = chargesQ.data?.data?.[0];

  if (!id) {
    return <div className="mx-auto max-w-xl py-12 text-center text-muted">Cliente inválido.</div>;
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-5">
        <ClientTableSkeleton rows={2} />
      </div>
    );
  }

  if (isError || !client) {
    return (
      <div className="mx-auto max-w-2xl py-12">
        <button
          type="button"
          onClick={() => navigate('/identities')}
          className="inline-flex items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
        >
          <ArrowLeft size={13} strokeWidth={2} />
          Identidades
        </button>
        <div className="mt-4 rounded-[14px] border border-bad/40 bg-panel p-6">
          <h2 className="text-[15px] font-semibold text-ink">
            Não conseguimos carregar esse cliente.
          </h2>
          <p className="mt-1 text-[12px] text-muted">
            {extractErrorMessage(error, 'Talvez ele tenha sido cancelado ou movido.')}
          </p>
          <div className="mt-4">
            <PillButton onClick={() => void refetch()}>Tentar de novo</PillButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <button
        type="button"
        onClick={() => navigate('/identities')}
        className="inline-flex w-fit items-center gap-1 text-[12px] text-muted transition-colors hover:text-ink"
      >
        <ArrowLeft size={13} strokeWidth={2} />
        Identidades
      </button>

      <ClientHeader
        client={client}
        onEdit={() => navigate(`/identities/${id}/edit`)}
        onCancel={() => setCancelOpen(true)}
        isCancelPending={cancel.isPending}
      />

      {/* Story 12.10 — override manual de acesso (admin/financeiro). */}
      <AccessOverrideActions
        clientId={client.id}
        clientStatus={client.status}
        hasOverdueDebt={lastCharge?.status === 'vencida'}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <StatCard
          label="MRR contribuído"
          value={subscriptionsQ.isLoading ? '…' : formatBRL(mrrContributed)}
          sub={
            activeSubs.length === 0
              ? 'Sem assinaturas ativas'
              : `${activeSubs.length} ativa${activeSubs.length === 1 ? '' : 's'}`
          }
        />
        <StatCard
          label="Assinaturas ativas"
          value={subscriptionsQ.isLoading ? '…' : String(activeSubs.length)}
          sub={
            subscriptionsItems.length > 0
              ? `${subscriptionsItems.length} no total`
              : 'Nenhuma assinatura'
          }
        />
        <StatCard
          label="Última cobrança"
          value={
            chargesQ.isLoading
              ? '…'
              : lastCharge
                ? formatBRL(Number.parseFloat(lastCharge.amount))
                : '—'
          }
          sub={
            lastCharge
              ? `${lastCharge.status} · venc. ${formatDayBR(lastCharge.dueDate)}`
              : 'Sem cobranças registradas'
          }
        />
      </div>

      <ClientTabs />

      <section
        role="tabpanel"
        id={`tab-${active}-panel`}
        aria-labelledby={`tab-${active}`}
        className="rounded-[14px] border border-line bg-panel p-5"
      >
        {active === 'dados' && <DataTab client={client} />}
        {active === 'contatos' && <ContactsTab clientId={id} />}
        {active === 'assinaturas-owl' && (
          <ClientSubscriptionsTab
            clientId={id}
            items={subscriptionsItems.filter((s) => s.origin === 'owl_managed')}
            isLoading={subscriptionsQ.isLoading}
            isError={subscriptionsQ.isError}
          />
        )}
        {active === 'assinaturas-direct' && (
          <ClientSubscriptionsTab
            clientId={id}
            items={subscriptionsItems.filter((s) => s.origin === 'saas_direct')}
            isLoading={subscriptionsQ.isLoading}
            isError={subscriptionsQ.isError}
            variant="saas_direct"
          />
        )}
        {active === 'cobrancas' && <ClientChargesTab clientId={id} />}
        {active === 'pagamentos' && <ClientPaymentsTab clientId={id} />}
        {active === 'comunicacoes' && (
          <CommunicationsList
            filters={{ clientId: id, pageSize: 50 }}
            hideClient
            emptyLabel="Sem comunicações registradas pra este cliente."
          />
        )}
        {active === 'vinculos-saas' && <SaasLinksTab clientId={id} />}
        {active === 'logs' && <LogsTab clientId={id} />}
      </section>

      <CancelClientDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        clientName={client.name}
        isPending={cancel.isPending}
        onConfirm={async () => {
          try {
            await cancel.mutateAsync({ id, version: client.version });
            toast.success('Cliente cancelado.', { description: client.name });
            setCancelOpen(false);
          } catch (err) {
            toast.error('Não conseguimos cancelar.', {
              description: extractErrorMessage(err, 'Tente de novo.'),
            });
          }
        }}
      />
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}): JSX.Element {
  return (
    <div className="rounded-[14px] border border-line bg-panel p-4">
      <div className="text-[10px] font-medium uppercase tracking-wider text-muted-3">{label}</div>
      <div className="tnum mt-[6px] text-[22px] font-bold tracking-[-0.025em] text-ink">
        {value}
      </div>
      {sub && <div className="mt-[3px] text-[11px] text-muted-2">{sub}</div>}
    </div>
  );
}
